import { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { IconHighlight, ModalLayout } from '@aimsos/ui';

import { useDismissModalOnRouteChange } from '../hooks';
import DashboardButton from './DashboardButton';
import { ModalFooter } from './parts';

import { Archive } from 'lucide-react';

/**
 * Confirm for archiving a dashboard (CT-5183 / DS-3).
 *
 * Shaped like `DeleteWidgetModal` in the widgets domain, which is the house
 * pattern: `ModalLayout` + `useBodyPortal`, the title in the header's own
 * slot with an icon beside it, the dashboard's name as the subtitle, and the
 * shared `ModalFooter` — so the actions sit right, an error has somewhere to
 * appear, and the primary button shows it is working.
 *
 * It used to hand-roll all of that inside a 900px `Modal`: a centred `<h2>`,
 * a centred pair of buttons, no error surface. `Modal` has `title` and
 * right-aligned actions of its own, so the divergence was not even buying
 * anything the component lacked.
 *
 * `variant="danger"` is deliberately **not** used: archiving is reversible
 * from the archived list, and dressing it in red would read as a delete.
 */
const ArchiveDashboardModal = ({
  open = false,
  onClose,
  onConfirm,
  dashboardName = '',
  loading = false,
  error = '',
}) => {
  const { t } = useTranslation('dashboards');
  useDismissModalOnRouteChange(open, onClose);

  const displayName = `${dashboardName || ''}`.trim() || t('common.dashboardNameFallback');

  if (!open) return null;

  return (
    <ModalLayout
      isOpen={open}
      onClose={onClose}
      useBodyPortal
      maxWidthClassName="max-w-md"
      titlePrefix={<IconHighlight icon={Archive} bgColor="Alert" iconColor="Alert" size="medium" />}
      title={t('modals.archive.title')}
      subtitle={displayName}
      closeDisabled={loading}
    >
      <div className="px-6 py-4">
        <p className="m-0 text-sm text-content-secondary">
          {t('modals.archive.body', { name: displayName })}
        </p>
      </div>

      <ModalFooter
        cancelLabel={t('actions.cancel')}
        onCancel={onClose}
        submitting={loading}
        submitError={error}
        primaryAction={
          <DashboardButton onClick={onConfirm}>{t('modals.archive.confirm')}</DashboardButton>
        }
      />
    </ModalLayout>
  );
};

ArchiveDashboardModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  dashboardName: PropTypes.string,
  loading: PropTypes.bool,
  /** Shown in the footer when the action failed; the dialog stays open. */
  error: PropTypes.string,
};

export default memo(ArchiveDashboardModal);
